import { useRef } from 'react'

interface StripPointerOptions {
  /** The item under the pointer, or null. */
  pick(event: React.PointerEvent<HTMLDivElement>): number | null
  count: number
  active: number | null
  pinned: number | null
  onHover(index: number | null): void
  /** Pin an item, or unpin with null. */
  onSelect(index: number | null): void
}

/**
 * Pointer and keyboard handling for a timeline strip of items. A mouse hovers
 * and clicks; a finger has no hover, so a drag scrubs through the items and
 * pins the one it lets go on, while a vertical swipe still scrolls the page.
 */
export function useStripPointer({
  pick,
  count,
  active,
  pinned,
  onHover,
  onSelect,
}: StripPointerOptions) {
  const drag = useRef<{ moved: boolean } | null>(null)

  const step = (direction: 1 | -1) => {
    if (count === 0) return
    const current = pinned ?? active
    onSelect(
      current === null
        ? direction === 1
          ? 0
          : count - 1
        : Math.min(count - 1, Math.max(0, current + direction))
    )
  }

  const handlers = {
    onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.pointerType === 'mouse') return
      drag.current = { moved: false }
      onHover(pick(event))
      if (event.currentTarget.hasPointerCapture(event.pointerId)) return
      try {
        event.currentTarget.setPointerCapture(event.pointerId)
      } catch {
        // The pointer is already gone; the drag simply ends on release.
      }
    },
    onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.pointerType !== 'mouse' && !drag.current) return
      if (drag.current) drag.current.moved = true
      onHover(pick(event))
    },
    onPointerUp: (event: React.PointerEvent<HTMLDivElement>) => {
      const index = pick(event)
      const moved = drag.current?.moved ?? false
      drag.current = null
      if (event.pointerType !== 'mouse') onHover(null)
      onSelect(!moved && index === pinned ? null : index)
    },
    onPointerCancel: () => {
      drag.current = null
      onHover(null)
    },
    onPointerLeave: (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.pointerType === 'mouse') onHover(null)
    },
    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowRight') step(1)
      else if (event.key === 'ArrowLeft') step(-1)
      else if (event.key === 'Escape') onSelect(null)
      else return
      event.preventDefault()
    },
  }

  return { handlers, step }
}
